import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import portalAPI, { portalOrdersAPI, portalFeedsAPI } from '../../services/portalAPI';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import PortalFooter from '../../components/Layout/PortalFooter';
import ShipmentTracking from '../../components/ShipmentTracking';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import '../../styles/portal-brand.css';

interface OrderItem {
  id: number;
  formula: number;
  formula_name: string;
  feed_grade: string;
  animal_type: string;
  is_pellet: boolean;
  total_quantity_lbs: number;
  number_of_packages: number;
  package_size_lbs: number;
  package_type?: string;
  unit_price_per_lb: string;
  price_per_package?: string | null;
  pricing_display_method?: string;
  line_total: string;
  store_product?: number | null;
  store_product_name?: string | null;
  item_source?: string;
}

interface WholesaleStoreProduct {
  id: number;
  name: string;
  sku: string;
  category: string;
  category_display: string;
  size_lbs: number;
  retail_price: string;
  effective_price: string;
  supplier: string;
}

interface StatusHistoryEntry {
  id: number;
  old_status: string | null;
  new_status: string;
  status_display: string;
  changed_at: string;
  changed_by_name: string;
  notes: string;
}

interface Order {
  id: number;
  order_number: string;
  order_date: string;
  delivery_date: string;
  status: string;
  status_display: string;
  customer_status_display: string;
  shipping_method: string;
  is_rush: boolean;
  is_tax_exempt: boolean;
  total_weight_lbs: number;
  estimated_pallets: number;
  pallet_discount?: { pallets: number; per_bag: number; bags: number; savings: number };
  subtotal: string;
  tax_amount: string;
  total_amount: string;
  special_instructions: string;
  customer_po_number: string;
  // Tracking info
  tracking_number: string | null;
  carrier: string | null;
  shipped_date: string | null;
  // Status history timeline
  status_history: StatusHistoryEntry[];
  items: OrderItem[];
}

// Define the status flow for the progress indicator
const STATUS_FLOW = [
  { key: 'PENDING', label: 'Order Received' },
  { key: 'CONFIRMED', label: 'Confirmed' },
  { key: 'IN_PRODUCTION', label: 'In Production' },
  { key: 'MILLED', label: 'Milled' },
  { key: 'PACKAGING', label: 'Packaging' },
  { key: 'READY_TO_SHIP', label: 'Ready' },
  { key: 'SHIPPED', label: 'Shipped' },
  { key: 'DELIVERED', label: 'Delivered' },
];

// Statuses that cannot be modified
const NON_MODIFIABLE_STATUSES = ['IN_PRODUCTION', 'PRODUCTION', 'MILLED', 'PACKAGING', 'READY_TO_SHIP', 'READY', 'SHIPPED', 'DELIVERED', 'CANCELLED'];

const PortalOrders: React.FC = () => {
  const { user } = usePortalAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Pagination & filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Edit/Cancel state
  const [isEditing, setIsEditing] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState<{
    delivery_date: string;
    special_instructions: string;
  } | null>(null);
  const [editingItems, setEditingItems] = useState<Map<number, number>>(new Map()); // itemId -> quantity
  const [removedItemIds, setRemovedItemIds] = useState<Set<number>>(new Set());
  const [showAddItem, setShowAddItem] = useState(false);
  const [availableFeeds, setAvailableFeeds] = useState<any[]>([]);
  const [newItemData, setNewItemData] = useState<{
    formula_id: number;
    quantity_lbs: number;
    package_type: string;
    is_pellet: boolean;
    custom_package_size: number;
  } | null>(null);
  // Progressive filtering state for Add Item
  const [selectedAnimalType, setSelectedAnimalType] = useState<string>('');
  const [selectedFeedLine, setSelectedFeedLine] = useState<string>('');
  // Item editing state
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editItemData, setEditItemData] = useState<{
    formula_id: number;
    quantity_lbs: number;
    package_type: string;
    animal_type: string;
    feed_line: string;
    is_pellet: boolean;
    custom_package_size: number;
  } | null>(null);
  // Customer equipment settings
  const [maxSupersackLbs, setMaxSupersackLbs] = useState<number>(2600);
  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  // Store products for wholesale add-ons
  const [wholesaleStoreProducts, setWholesaleStoreProducts] = useState<WholesaleStoreProduct[]>([]);
  const [showStoreProducts, setShowStoreProducts] = useState(false);
  const [addingStoreProductId, setAddingStoreProductId] = useState<number | null>(null);

  useEffect(() => {
    loadOrders();
    loadCustomerSettings();
  }, [currentPage, statusFilter, searchQuery]);

  const loadCustomerSettings = async () => {
    try {
      const response = await portalAPI.get('/portal/delivery-settings/');
      if (response.data?.max_supersack_lbs) {
        setMaxSupersackLbs(response.data.max_supersack_lbs);
      }
    } catch (err) {
      console.error('Failed to load customer settings:', err);
      // Keep default value
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const params: { page: number; page_size: number; status?: string; search?: string } = {
        page: currentPage,
        page_size: pageSize,
      };
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await portalOrdersAPI.getAll(params);
      const data = response.data;
      if (data.results) {
        setOrders(data.results);
        setTotalCount(data.count || 0);
      } else {
        // Fallback for unpaginated response
        const arr = Array.isArray(data) ? data : [];
        setOrders(arr);
        setTotalCount(arr.length);
      }
    } catch (err: any) {
      setError('Failed to load orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const viewOrderDetails = async (orderId: number) => {
    try {
      const response = await portalOrdersAPI.getById(orderId);
      setSelectedOrder(response.data);
    } catch (err: any) {
      console.error('Failed to load order details:', err);
    }
  };

  const formatItemPricing = (item: OrderItem) => {
    const lineTotal = parseFloat(item.line_total);

    // Use pricing_display_method from backend, fall back to package_type check
    const isBaggedOrder = item.pricing_display_method === 'PER_PACKAGE' ||
      ((!item.pricing_display_method) && (item.package_type === 'BAGS' || item.package_type === 'BAGS_19'));

    // For bagged orders, show price per bag
    if (isBaggedOrder && item.number_of_packages > 0) {
      const pricePerBag = item.price_per_package ? parseFloat(item.price_per_package) : (lineTotal / item.number_of_packages);
      return `$${pricePerBag.toFixed(2)}/bag × ${item.number_of_packages} bags = $${lineTotal.toFixed(2)}`;
    }

    // For super sacks, bulk, and BYOC - show price per pound
    const pricePerLb = parseFloat(item.unit_price_per_lb) || (item.total_quantity_lbs > 0 ? lineTotal / item.total_quantity_lbs : 0);
    return `$${pricePerLb.toFixed(4)}/lb × ${item.total_quantity_lbs.toLocaleString()} lbs = $${lineTotal.toFixed(2)}`;
  };

  const closeDetails = () => {
    setSelectedOrder(null);
  };

  const handleReorder = (order: Order) => {
    if (!user?.permissions.can_place_orders) {
      alert('You do not have permission to place orders');
      return;
    }

    // Store order data in localStorage to pre-populate the new order form
    const reorderData = {
      shipping_method: order.shipping_method,
      is_rush: order.is_rush,
      special_instructions: order.special_instructions,
      items: order.items.map(item => ({
        formula: item.formula,
        formula_name: item.formula_name,
        feed_grade: item.feed_grade,
        animal_type: item.animal_type,
        is_pellet: item.is_pellet,
        number_of_packages: item.number_of_packages,
        package_size_lbs: item.package_size_lbs,
      })),
    };

    localStorage.setItem('reorder_data', JSON.stringify(reorderData));
    navigate('/portal/place-order');
  };

  const isOrderModifiable = (order: Order): boolean => {
    return !NON_MODIFIABLE_STATUSES.includes(order.status);
  };

  const startEditing = async () => {
    if (!selectedOrder) return;
    setEditData({
      delivery_date: selectedOrder.delivery_date,
      special_instructions: selectedOrder.special_instructions || '',
    });
    // Initialize item quantities from current order
    const itemQuantities = new Map<number, number>();
    selectedOrder.items.forEach(item => {
      itemQuantities.set(item.id, item.total_quantity_lbs);
    });
    setEditingItems(itemQuantities);
    setRemovedItemIds(new Set());
    setIsEditing(true);
    setWarningMessage(selectedOrder.status === 'CONFIRMED'
      ? 'Order already confirmed. Changes may delay processing.'
      : '');

    // Load available feeds for adding new items
    try {
      const response = await portalFeedsAPI.getAll();
      setAvailableFeeds(response.data.results || response.data || []);
    } catch (err) {
      console.error('Failed to load available feeds:', err);
    }

    // Load wholesale store products (non-blocking)
    portalAPI.get('/portal/available-store-products/').then(res => {
      setWholesaleStoreProducts(res.data || []);
    }).catch(() => {});
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditData(null);
    setEditingItems(new Map());
    setRemovedItemIds(new Set());
    setShowAddItem(false);
    setNewItemData(null);
    setSelectedAnimalType('');
    setSelectedFeedLine('');
    setWarningMessage('');
  };

  const saveChanges = async () => {
    if (!selectedOrder || !editData) return;

    try {
      setSaving(true);

      // 1. Update order details (delivery date, instructions)
      await portalOrdersAPI.update(selectedOrder.id, editData);

      // 2. Remove deleted items
      for (const itemId of Array.from(removedItemIds)) {
        try {
          await portalOrdersAPI.removeItem(selectedOrder.id, itemId);
        } catch (err) {
          console.error(`Failed to remove item ${itemId}:`, err);
        }
      }

      // 3. Update modified items
      for (const [itemId, newQuantity] of Array.from(editingItems)) {
        if (removedItemIds.has(itemId)) continue; // Skip removed items
        const originalItem = selectedOrder.items.find(i => i.id === itemId);
        if (originalItem && originalItem.total_quantity_lbs !== newQuantity) {
          try {
            await portalOrdersAPI.updateItem(selectedOrder.id, itemId, {
              total_quantity_lbs: newQuantity,
            });
          } catch (err) {
            console.error(`Failed to update item ${itemId}:`, err);
          }
        }
      }

      // 4. Reload the order to get updated data
      const response = await portalOrdersAPI.getById(selectedOrder.id);

      // Update the order in state
      setSelectedOrder(response.data);
      setOrders(prev => prev.map(o => o.id === selectedOrder.id ? response.data : o));

      setIsEditing(false);
      setEditData(null);
      setEditingItems(new Map());
      setRemovedItemIds(new Set());
      setShowAddItem(false);
      setNewItemData(null);
      setSuccessMessage('Order updated successfully');
      if (response.data.warning) {
        setWarningMessage(response.data.warning);
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const updateItemQuantity = (itemId: number, quantity: number) => {
    setEditingItems(prev => {
      const newMap = new Map(prev);
      newMap.set(itemId, Math.max(0, quantity));
      return newMap;
    });
  };

  const removeItem = (itemId: number) => {
    setRemovedItemIds(prev => new Set(prev).add(itemId));
  };

  const undoRemoveItem = (itemId: number) => {
    setRemovedItemIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  };

  const handleAddItem = async () => {
    if (!selectedOrder || !newItemData || !newItemData.formula_id || !newItemData.package_type) return;

    try {
      setSaving(true);

      // Calculate number of packages based on package type
      const packageSizeMap: Record<string, number> = {
        'BAGS': 40,
        'SUPERSACK_1000': 1000,
        'SUPERSACK_2000': 2000,
        'SUPERSACK_2600': 2600,
      };
      // For custom supersack, use the custom size; otherwise use the map
      const packageSize = newItemData.package_type === 'SUPERSACK_CUSTOM'
        ? (newItemData.custom_package_size || maxSupersackLbs)
        : (packageSizeMap[newItemData.package_type] || 40);
      const numberOfPackages = Math.ceil(newItemData.quantity_lbs / packageSize);

      await portalOrdersAPI.addItem(selectedOrder.id, {
        formula: newItemData.formula_id,  // Backend expects 'formula' not 'formula_id'
        total_quantity_lbs: newItemData.quantity_lbs,
        package_type: newItemData.package_type,  // Backend expects enum like 'BAGS', 'SUPERSACK_1000', 'SUPERSACK_CUSTOM'
        number_of_packages: numberOfPackages,
        is_pellet: newItemData.is_pellet,
        ...(newItemData.package_type === 'SUPERSACK_CUSTOM' && {
          package_size_lbs: newItemData.custom_package_size || maxSupersackLbs,
        }),
      });

      // Reload order to get updated items
      const response = await portalOrdersAPI.getById(selectedOrder.id);
      setSelectedOrder(response.data);

      // Update editing items map with new item
      const itemQuantities = new Map(editingItems);
      response.data.items.forEach((item: OrderItem) => {
        if (!itemQuantities.has(item.id)) {
          itemQuantities.set(item.id, item.total_quantity_lbs);
        }
      });
      setEditingItems(itemQuantities);

      setShowAddItem(false);
      setNewItemData(null);
      setSelectedAnimalType('');
      setSelectedFeedLine('');
      setSuccessMessage('Item added to order');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Failed to add item:', err.response?.data);
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to add item');
    } finally {
      setSaving(false);
    }
  };

  const handleAddStoreProduct = async (product: WholesaleStoreProduct) => {
    if (!selectedOrder) return;
    try {
      setAddingStoreProductId(product.id);
      await portalOrdersAPI.addItem(selectedOrder.id, {
        store_product_id: product.id,
        quantity: 1,
      } as any);
      // Reload order
      const response = await portalOrdersAPI.getById(selectedOrder.id);
      setSelectedOrder(response.data);
      const itemQuantities = new Map(editingItems);
      response.data.items.forEach((item: OrderItem) => {
        if (!itemQuantities.has(item.id)) {
          itemQuantities.set(item.id, item.total_quantity_lbs);
        }
      });
      setEditingItems(itemQuantities);
      setSuccessMessage(`Added ${product.name} to order`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add store product');
    } finally {
      setAddingStoreProductId(null);
    }
  };

  // Helper to get available forms for a formula
  const getAvailableForms = (formulaId: number): string[] => {
    const formula = availableFeeds.find(f => f.id === formulaId);
    if (!formula) return ['MASH', 'PELLET'];
    const forms = formula.available_forms;
    if (Array.isArray(forms) && forms.length > 0) {
      return forms.map((f: string) => f.toUpperCase());
    }
    return ['MASH', 'PELLET']; // Default to both if not specified
  };

  const startEditingItem = (item: OrderItem) => {
    setEditingItemId(item.id);
    // Determine custom package size from the item's package_size_lbs if it's a custom supersack
    const customSize = item.package_type === 'SUPERSACK_CUSTOM'
      ? (item.package_size_lbs || maxSupersackLbs)
      : maxSupersackLbs;
    setEditItemData({
      formula_id: item.formula,
      quantity_lbs: item.total_quantity_lbs,
      package_type: item.package_type || 'BAGS',
      animal_type: item.animal_type,
      feed_line: item.feed_grade,
      is_pellet: item.is_pellet,
      custom_package_size: customSize,
    });
  };

  const cancelEditingItem = () => {
    setEditingItemId(null);
    setEditItemData(null);
  };

  const saveItemEdit = async () => {
    if (!selectedOrder || !editingItemId || !editItemData) return;

    try {
      setSaving(true);

      // Calculate number of packages based on package type
      const packageSizeMap: Record<string, number> = {
        'BAGS': 40,
        'SUPERSACK_1000': 1000,
        'SUPERSACK_2000': 2000,
        'SUPERSACK_2600': 2600,
      };
      // For custom supersack, use the custom size; otherwise use the map
      const packageSize = editItemData.package_type === 'SUPERSACK_CUSTOM'
        ? (editItemData.custom_package_size || maxSupersackLbs)
        : (packageSizeMap[editItemData.package_type] || 40);
      const numberOfPackages = Math.ceil(editItemData.quantity_lbs / packageSize);

      await portalOrdersAPI.updateItem(selectedOrder.id, editingItemId, {
        formula: editItemData.formula_id,
        total_quantity_lbs: editItemData.quantity_lbs,
        package_type: editItemData.package_type,
        number_of_packages: numberOfPackages,
        is_pellet: editItemData.is_pellet,
        ...(editItemData.package_type === 'SUPERSACK_CUSTOM' && {
          package_size_lbs: editItemData.custom_package_size || maxSupersackLbs,
        }),
      });

      // Reload order
      const response = await portalOrdersAPI.getById(selectedOrder.id);
      setSelectedOrder(response.data);

      setEditingItemId(null);
      setEditItemData(null);
      setSuccessMessage('Item updated');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Failed to update item:', err.response?.data);
      setError(err.response?.data?.error || 'Failed to update item');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;

    try {
      setSaving(true);
      await portalOrdersAPI.cancel(selectedOrder.id, cancelReason);

      // Remove cancelled order from list and close modal
      setOrders(prev => prev.filter(o => o.id !== selectedOrder.id));
      setSelectedOrder(null);
      setCancelConfirmOpen(false);
      setCancelReason('');
      setSuccessMessage(`Order #${selectedOrder.order_number} has been cancelled`);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel order');
      setCancelConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading orders...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <TutorialAutoStart pageKey="orders" />

      <div style={styles.header}>
        <h1 style={styles.title}>My Orders</h1>
        <TutorialHelpButton pageKey="orders" />
      </div>

      {/* Filters & Search */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          style={{
            padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd',
            fontSize: 14, background: '#fff', minWidth: 160,
          }}
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="pending_review">Pending Review</option>
          <option value="confirmed">Confirmed</option>
          <option value="in_production">In Production</option>
          <option value="shipped">Shipped / Ready</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 300 }}>
          <input
            type="text"
            placeholder="Search order #..."
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
              searchDebounceRef.current = setTimeout(() => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }, 400);
            }}
            style={{
              padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd',
              fontSize: 14, width: '100%', boxSizing: 'border-box',
            }}
          />
        </div>
        {totalCount > 0 && (
          <span style={{ fontSize: 13, color: '#666', marginLeft: 'auto' }}>
            {totalCount} order{totalCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {!orders || !Array.isArray(orders) || orders.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>{statusFilter || searchQuery ? 'No orders match your filters' : 'No orders found'}</p>
          {!statusFilter && !searchQuery && (
            <Link to="/portal/place-order" style={styles.placeOrderButton}>
              Place Your First Order
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="portal-table-desktop" style={styles.table} data-tutorial="orders-table">
            <table style={styles.tableElement}>
              <thead>
                <tr>
                  <th style={styles.th}>Order #</th>
                  <th style={styles.th}>Order Date</th>
                  <th style={styles.th}>Delivery Date</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Total Weight</th>
                  <th style={styles.th}>Total Amount</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, index) => (
                  <tr key={order.id}>
                    <td style={styles.td}>{order.order_number}</td>
                    <td style={styles.td}>{new Date(order.order_date).toLocaleDateString()}</td>
                    <td style={styles.td}>{new Date(order.delivery_date).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      <span style={getStatusStyle(order.status)} data-tutorial={index === 0 ? "status-badge" : undefined}>{order.customer_status_display || order.status_display}</span>
                    </td>
                    <td style={styles.td}>{order.total_weight_lbs.toLocaleString()} lbs</td>
                    <td style={styles.td}>${parseFloat(order.total_amount).toFixed(2)}</td>
                    <td style={styles.td}>
                      <div style={styles.buttonGroup} data-tutorial={index === 0 ? "order-actions" : undefined}>
                        <button
                          onClick={() => viewOrderDetails(order.id)}
                          style={styles.viewButton}
                        >
                          View Details
                        </button>
                        {user?.permissions.can_place_orders && (
                          <button
                            onClick={() => handleReorder(order)}
                            style={styles.reorderButton}
                            title="Create a new order with the same items"
                          >
                            Reorder
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="portal-cards-mobile">
            {orders.map((order) => (
              <div key={order.id} className="portal-mobile-card">
                <div className="portal-mobile-card-header">
                  <span className="portal-mobile-card-title">{order.order_number}</span>
                  <span style={getStatusStyle(order.status)}>{order.customer_status_display || order.status_display}</span>
                </div>
                <div className="portal-mobile-card-row">
                  <span className="portal-mobile-card-label">Order Date</span>
                  <span className="portal-mobile-card-value">{new Date(order.order_date).toLocaleDateString()}</span>
                </div>
                <div className="portal-mobile-card-row">
                  <span className="portal-mobile-card-label">Delivery Date</span>
                  <span className="portal-mobile-card-value">{new Date(order.delivery_date).toLocaleDateString()}</span>
                </div>
                <div className="portal-mobile-card-row">
                  <span className="portal-mobile-card-label">Total Weight</span>
                  <span className="portal-mobile-card-value">{order.total_weight_lbs.toLocaleString()} lbs</span>
                </div>
                <div className="portal-mobile-card-row">
                  <span className="portal-mobile-card-label">Total Amount</span>
                  <span className="portal-mobile-card-value" style={{ fontWeight: 'bold', color: '#E73C3E' }}>${parseFloat(order.total_amount).toFixed(2)}</span>
                </div>
                <div className="portal-mobile-card-actions">
                  <button onClick={() => viewOrderDetails(order.id)} style={styles.viewButton}>
                    View Details
                  </button>
                  {user?.permissions.can_place_orders && (
                    <button onClick={() => handleReorder(order)} style={styles.reorderButton}>
                      Reorder
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalCount > pageSize && (() => {
            const totalPages = Math.ceil(totalCount / pageSize);
            const startItem = (currentPage - 1) * pageSize + 1;
            const endItem = Math.min(currentPage * pageSize, totalCount);
            return (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 0', marginTop: 8, flexWrap: 'wrap', gap: 8,
              }}>
                <span style={{ fontSize: 13, color: '#666' }}>
                  Showing {startItem}-{endItem} of {totalCount} orders
                </span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: '1px solid #ddd',
                      background: currentPage <= 1 ? '#f5f5f5' : '#fff', cursor: currentPage <= 1 ? 'default' : 'pointer',
                      fontSize: 13, color: currentPage <= 1 ? '#bbb' : '#333',
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: '1px solid #ddd',
                      background: currentPage >= totalPages ? '#f5f5f5' : '#fff', cursor: currentPage >= totalPages ? 'default' : 'pointer',
                      fontSize: 13, color: currentPage >= totalPages ? '#bbb' : '#333',
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            );
          })()}
        </>
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <div style={styles.modal} onClick={closeDetails}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Order Details - {selectedOrder.order_number}</h2>
              <button onClick={closeDetails} style={styles.closeButton}>×</button>
            </div>

            <div style={styles.modalBody}>
              {/* Success/Warning Messages */}
              {successMessage && (
                <div style={styles.successBanner}>{successMessage}</div>
              )}
              {warningMessage && (
                <div style={styles.warningBanner}>{warningMessage}</div>
              )}

              {/* Current Status Banner */}
              <div style={styles.statusBanner}>
                <div style={styles.statusBannerIcon}>
                  {getStatusIcon(selectedOrder.status)}
                </div>
                <div style={styles.statusBannerText}>
                  <span style={styles.statusBannerLabel}>Current Status</span>
                  <span style={getStatusStyle(selectedOrder.status)}>
                    {selectedOrder.customer_status_display || selectedOrder.status_display}
                  </span>
                </div>
              </div>

              {/* Edit/Cancel Buttons or Non-Modifiable Warning */}
              {isOrderModifiable(selectedOrder) ? (
                <div style={styles.actionButtonsRow}>
                  {!isEditing ? (
                    <>
                      <button onClick={startEditing} style={styles.editButton}>
                        Edit Order
                      </button>
                      <button onClick={() => setCancelConfirmOpen(true)} style={styles.cancelOrderButton}>
                        Cancel Order
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={saveChanges} disabled={saving} style={styles.saveButton}>
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button onClick={cancelEditing} disabled={saving} style={styles.cancelEditButton}>
                        Cancel Edit
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div style={styles.nonModifiableBanner}>
                  This order is in production and cannot be modified. Contact the mill at (509) 738-9004 for assistance.
                </div>
              )}

              {/* Edit Form */}
              {isEditing && editData && (
                <div style={styles.editForm}>
                  <h3 style={styles.editFormTitle}>Edit Order Details</h3>
                  <div style={styles.editFormGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Delivery Date</label>
                      <input
                        type="date"
                        value={editData.delivery_date}
                        onChange={(e) => setEditData({ ...editData, delivery_date: e.target.value })}
                        style={styles.formInput}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Special Instructions</label>
                      <textarea
                        value={editData.special_instructions}
                        onChange={(e) => setEditData({ ...editData, special_instructions: e.target.value })}
                        style={styles.formTextarea}
                        rows={3}
                        placeholder="Any special delivery or handling instructions..."
                      />
                    </div>
                  </div>

                  {/* Order Items Editing Section */}
                  <div style={styles.itemsEditSection}>
                    <div style={styles.itemsEditHeader}>
                      <h4 style={styles.itemsEditTitle}>Order Items</h4>
                      <button
                        onClick={() => setShowAddItem(true)}
                        style={styles.addItemButton}
                        disabled={saving}
                      >
                        + Add Item
                      </button>
                    </div>

                    {selectedOrder.items.map((item) => {
                      const isRemoved = removedItemIds.has(item.id);
                      const currentQuantity = editingItems.get(item.id) ?? item.total_quantity_lbs;
                      const isEditingThisItem = editingItemId === item.id;

                      return (
                        <div
                          key={item.id}
                          style={{
                            ...styles.itemEditRow,
                            ...(isRemoved ? styles.itemEditRowRemoved : {}),
                            ...(isEditingThisItem ? { flexDirection: 'column', alignItems: 'stretch' } : {}),
                          }}
                        >
                          {isEditingThisItem && editItemData ? (
                            // EDIT MODE - show form
                            <div style={{ padding: '0.5rem 0' }}>
                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                                {/* Animal Type */}
                                <select
                                  value={editItemData.animal_type}
                                  onChange={(e) => {
                                    setEditItemData({ ...editItemData, animal_type: e.target.value, formula_id: 0 });
                                  }}
                                  style={styles.animalTypeSelect}
                                  disabled={saving}
                                >
                                  <option value="">Animal Type...</option>
                                  {Array.from(new Set(availableFeeds.map(f => f.animal_type))).map(type => (
                                    <option key={type} value={type}>{type}</option>
                                  ))}
                                </select>

                                {/* Feed Line */}
                                {editItemData.animal_type && (
                                  <select
                                    value={editItemData.feed_line}
                                    onChange={(e) => {
                                      setEditItemData({ ...editItemData, feed_line: e.target.value, formula_id: 0 });
                                    }}
                                    style={styles.feedLineSelect}
                                    disabled={saving}
                                  >
                                    <option value="">Feed Line...</option>
                                    {Array.from(new Set(
                                      availableFeeds
                                        .filter(f => f.animal_type === editItemData.animal_type)
                                        .map(f => f.feed_grade)
                                    )).map(line => (
                                      <option key={line} value={line}>{line}</option>
                                    ))}
                                  </select>
                                )}

                                {/* Formula */}
                                {editItemData.animal_type && editItemData.feed_line && (
                                  <select
                                    value={editItemData.formula_id || ''}
                                    onChange={(e) => {
                                      setEditItemData({ ...editItemData, formula_id: parseInt(e.target.value) });
                                    }}
                                    style={styles.feedSelect}
                                    disabled={saving}
                                  >
                                    <option value="">Formula...</option>
                                    {availableFeeds
                                      .filter(f => f.animal_type === editItemData.animal_type && f.feed_grade === editItemData.feed_line)
                                      .map(f => (
                                        <option key={f.id} value={f.id}>{f.name}</option>
                                      ))}
                                  </select>
                                )}
                              </div>

                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                <input
                                  type="number"
                                  value={editItemData.quantity_lbs}
                                  onChange={(e) => setEditItemData({ ...editItemData, quantity_lbs: parseInt(e.target.value) || 0 })}
                                  style={styles.addQuantityInput}
                                  min="1"
                                  disabled={saving}
                                />
                                <span style={styles.quantityLabel}>lbs</span>

                                {/* Form selector - disabled if only one form available */}
                                {(() => {
                                  const forms = getAvailableForms(editItemData.formula_id);
                                  const formDisabled = forms.length === 1;
                                  return (
                                    <select
                                      value={editItemData.is_pellet ? 'PELLET' : 'MASH'}
                                      onChange={(e) => setEditItemData({ ...editItemData, is_pellet: e.target.value === 'PELLET' })}
                                      style={{
                                        ...styles.packageSelect,
                                        ...(formDisabled ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}),
                                      }}
                                      disabled={saving || formDisabled}
                                      title={formDisabled ? `Only ${forms[0].toLowerCase()} available` : 'Select form'}
                                    >
                                      {forms.includes('MASH') && <option value="MASH">Mash</option>}
                                      {forms.includes('PELLET') && <option value="PELLET">Pellet</option>}
                                    </select>
                                  );
                                })()}

                                <select
                                  value={editItemData.package_type}
                                  onChange={(e) => setEditItemData({
                                    ...editItemData,
                                    package_type: e.target.value,
                                    // Set default custom size when switching to custom
                                    custom_package_size: e.target.value === 'SUPERSACK_CUSTOM'
                                      ? (editItemData.custom_package_size || maxSupersackLbs)
                                      : editItemData.custom_package_size,
                                  })}
                                  style={styles.packageSelect}
                                  disabled={saving}
                                >
                                  <option value="BAGS">40 lb Bags</option>
                                  <option value="SUPERSACK_1000">Supersack (1,000 lb)</option>
                                  <option value="SUPERSACK_2000">Supersack (2,000 lb)</option>
                                  <option value="SUPERSACK_2600">Supersack (2,600 lb)</option>
                                  <option value="SUPERSACK_CUSTOM">Custom Supersack</option>
                                </select>

                                {/* Custom supersack size input */}
                                {editItemData.package_type === 'SUPERSACK_CUSTOM' && (
                                  <input
                                    type="number"
                                    value={editItemData.custom_package_size || ''}
                                    onChange={(e) => setEditItemData({
                                      ...editItemData,
                                      custom_package_size: parseInt(e.target.value) || 0,
                                    })}
                                    placeholder={`lbs (max ${maxSupersackLbs})`}
                                    min="500"
                                    max={maxSupersackLbs}
                                    style={{
                                      ...styles.addQuantityInput,
                                      width: '100px',
                                    }}
                                    disabled={saving}
                                    title={`Max supersack size for your equipment: ${maxSupersackLbs} lbs`}
                                  />
                                )}

                                <button
                                  type="button"
                                  onClick={saveItemEdit}
                                  style={styles.confirmAddButton}
                                  disabled={saving || !editItemData.formula_id || !editItemData.quantity_lbs}
                                >
                                  {saving ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEditingItem}
                                  style={styles.cancelAddButton}
                                  disabled={saving}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            // VIEW MODE
                            <>
                              <div style={styles.itemEditInfo}>
                                <span style={styles.itemEditName}>
                                  {item.store_product_name || item.formula_name}
                                  {item.item_source === 'store' && <span style={{ marginLeft: '4px', padding: '1px 6px', borderRadius: '3px', background: '#dbeafe', color: '#1d4ed8', fontSize: '10px', fontWeight: 600 }}>(Store)</span>}
                                </span>
                                <span style={styles.itemEditMeta}>
                                  {item.item_source === 'store' ? 'Store Product' : `${item.feed_grade} - ${item.animal_type} - ${item.is_pellet ? 'Pellet' : 'Mash'}`}
                                </span>
                              </div>
                              {isRemoved ? (
                                <div style={styles.itemRemovedActions}>
                                  <span style={styles.removedLabel}>Removed</span>
                                  <button
                                    onClick={() => undoRemoveItem(item.id)}
                                    style={styles.undoButton}
                                    disabled={saving}
                                  >
                                    Undo
                                  </button>
                                </div>
                              ) : (
                                <div style={styles.itemEditActions}>
                                  <input
                                    type="number"
                                    value={currentQuantity}
                                    onChange={(e) => updateItemQuantity(item.id, parseInt(e.target.value) || 0)}
                                    style={styles.quantityInput}
                                    min="0"
                                    disabled={saving}
                                  />
                                  <span style={styles.quantityLabel}>lbs</span>
                                  <button
                                    type="button"
                                    onClick={() => startEditingItem(item)}
                                    style={{ ...styles.undoButton, marginLeft: '4px' }}
                                    disabled={saving}
                                    title="Edit feed type"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeItem(item.id)}
                                    style={styles.removeItemButton}
                                    disabled={saving || selectedOrder.items.length <= 1}
                                    title={selectedOrder.items.length <= 1 ? 'Cannot remove last item' : 'Remove item'}
                                  >
                                    ✕
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}

                    {/* Add Item Form with Progressive Filtering */}
                    {showAddItem && (
                      <div style={styles.addItemForm}>
                        <h5 style={styles.addItemTitle}>Add New Item</h5>
                        <div style={styles.addItemFields}>
                          {/* Step 1: Animal Type */}
                          <select
                            value={selectedAnimalType}
                            onChange={(e) => {
                              setSelectedAnimalType(e.target.value);
                              setSelectedFeedLine('');
                              setNewItemData(null);
                            }}
                            style={styles.feedSelect}
                            disabled={saving}
                          >
                            <option value="">1. Select Animal Type...</option>
                            {Array.from(new Set(availableFeeds.map(f => f.animal_type))).map((type) => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>

                          {/* Step 2: Feed Line (filtered by animal type) */}
                          {selectedAnimalType && (
                            <select
                              value={selectedFeedLine}
                              onChange={(e) => {
                                setSelectedFeedLine(e.target.value);
                                setNewItemData(null);
                              }}
                              style={styles.feedSelect}
                              disabled={saving}
                            >
                              <option value="">2. Select Feed Line...</option>
                              {Array.from(new Set(
                                availableFeeds
                                  .filter(f => f.animal_type === selectedAnimalType)
                                  .map(f => f.feed_grade)
                              )).map((grade) => (
                                <option key={grade} value={grade}>
                                  {grade === 'FIVE_STAR' ? 'Five Star' :
                                   grade === 'PLUS' ? 'Plus' :
                                   grade === 'ESSENTIALS' ? 'Essentials' : grade}
                                </option>
                              ))}
                            </select>
                          )}

                          {/* Step 3: Formula (filtered by animal type + feed line) */}
                          {selectedAnimalType && selectedFeedLine && (
                            <select
                              value={newItemData?.formula_id || ''}
                              onChange={(e) => {
                                const formulaId = parseInt(e.target.value);
                                const forms = getAvailableForms(formulaId);
                                // Default to MASH if available, otherwise first available form
                                const defaultForm = forms.includes('MASH') ? false : forms[0] === 'PELLET';
                                setNewItemData({
                                  formula_id: formulaId,
                                  quantity_lbs: newItemData?.quantity_lbs || 500,
                                  package_type: 'BAGS',
                                  is_pellet: defaultForm,
                                  custom_package_size: maxSupersackLbs,
                                });
                              }}
                              style={styles.feedSelect}
                              disabled={saving}
                            >
                              <option value="">3. Select Formula...</option>
                              {availableFeeds
                                .filter(f => f.animal_type === selectedAnimalType && f.feed_grade === selectedFeedLine)
                                .map((feed) => (
                                  <option key={feed.id} value={feed.id}>
                                    {feed.name}
                                  </option>
                                ))}
                            </select>
                          )}

                          {/* Step 4: Quantity, Form, and Package Size */}
                          {newItemData?.formula_id && (
                            <>
                              <input
                                type="number"
                                placeholder="Quantity (lbs)"
                                value={newItemData?.quantity_lbs || ''}
                                onChange={(e) => setNewItemData({
                                  ...newItemData!,
                                  quantity_lbs: parseInt(e.target.value) || 0,
                                })}
                                style={styles.addQuantityInput}
                                min="1"
                                disabled={saving}
                              />
                              {/* Form selector - disabled if only one form available */}
                              {(() => {
                                const forms = getAvailableForms(newItemData.formula_id);
                                const formDisabled = forms.length === 1;
                                return (
                                  <select
                                    value={newItemData.is_pellet ? 'PELLET' : 'MASH'}
                                    onChange={(e) => setNewItemData({
                                      ...newItemData!,
                                      is_pellet: e.target.value === 'PELLET',
                                    })}
                                    style={{
                                      ...styles.packageSelect,
                                      ...(formDisabled ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}),
                                    }}
                                    disabled={saving || formDisabled}
                                    title={formDisabled ? `Only ${forms[0].toLowerCase()} available` : 'Select form'}
                                  >
                                    {forms.includes('MASH') && <option value="MASH">Mash</option>}
                                    {forms.includes('PELLET') && <option value="PELLET">Pellet</option>}
                                  </select>
                                );
                              })()}
                              <select
                                value={newItemData?.package_type || ''}
                                onChange={(e) => setNewItemData({
                                  ...newItemData!,
                                  package_type: e.target.value,
                                  // Set default custom size when switching to custom
                                  custom_package_size: e.target.value === 'SUPERSACK_CUSTOM'
                                    ? (newItemData?.custom_package_size || maxSupersackLbs)
                                    : (newItemData?.custom_package_size || 0),
                                })}
                                style={styles.packageSelect}
                                disabled={saving}
                              >
                                <option value="">Packaging...</option>
                                <option value="BAGS">40 lb Bags</option>
                                <option value="SUPERSACK_1000">Supersack (1,000 lb)</option>
                                <option value="SUPERSACK_2000">Supersack (2,000 lb)</option>
                                <option value="SUPERSACK_2600">Supersack (2,600 lb)</option>
                                <option value="SUPERSACK_CUSTOM">Custom Supersack</option>
                              </select>

                              {/* Custom supersack size input */}
                              {newItemData?.package_type === 'SUPERSACK_CUSTOM' && (
                                <input
                                  type="number"
                                  value={newItemData.custom_package_size || ''}
                                  onChange={(e) => setNewItemData({
                                    ...newItemData,
                                    custom_package_size: parseInt(e.target.value) || 0,
                                  })}
                                  placeholder={`lbs (max ${maxSupersackLbs})`}
                                  min="500"
                                  max={maxSupersackLbs}
                                  style={{
                                    ...styles.addQuantityInput,
                                    width: '100px',
                                  }}
                                  disabled={saving}
                                  title={`Max supersack size for your equipment: ${maxSupersackLbs} lbs`}
                                />
                              )}
                            </>
                          )}
                        </div>
                        <div style={styles.addItemButtons}>
                          <button
                            type="button"
                            onClick={handleAddItem}
                            style={styles.confirmAddButton}
                            disabled={saving || !newItemData?.formula_id || !newItemData?.quantity_lbs || !newItemData?.package_type}
                          >
                            {saving ? 'Adding...' : 'Add to Order'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddItem(false);
                              setNewItemData(null);
                              setSelectedAnimalType('');
                              setSelectedFeedLine('');
                            }}
                            style={styles.cancelAddButton}
                            disabled={saving}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Store Products Add-On Section */}
                    {wholesaleStoreProducts.length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <button
                          type="button"
                          onClick={() => setShowStoreProducts(!showStoreProducts)}
                          style={{
                            background: 'none', border: '1px solid #d1d5db', padding: '6px 12px',
                            borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: '#374151',
                          }}
                        >
                          {showStoreProducts ? 'Hide' : 'Show'} Add-On Products ({wholesaleStoreProducts.length})
                        </button>
                        {showStoreProducts && (
                          <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px' }}>
                            {wholesaleStoreProducts.map(prod => (
                              <div key={prod.id} style={{
                                padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0',
                                background: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              }}>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: '13px' }}>{prod.name}</div>
                                  <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                    {prod.size_lbs} lbs &middot; ${prod.effective_price}
                                    <span style={{ marginLeft: '4px', padding: '1px 4px', borderRadius: '3px', background: '#dbeafe', color: '#1d4ed8', fontSize: '10px' }}>(Store)</span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleAddStoreProduct(prod)}
                                  disabled={saving || addingStoreProductId === prod.id}
                                  style={{
                                    padding: '4px 10px', borderRadius: '4px', border: '1px solid #6C8062',
                                    background: '#f0fdf4', color: '#6C8062', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                  }}
                                >
                                  {addingStoreProductId === prod.id ? '...' : 'Add'}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Status Progress Bar */}
              <div style={styles.progressContainer}>
                <div style={styles.progressBar}>
                  {STATUS_FLOW.map((step, index) => {
                    const currentIndex = STATUS_FLOW.findIndex(s =>
                      s.key === selectedOrder.status ||
                      (selectedOrder.status === 'PRODUCTION' && s.key === 'IN_PRODUCTION') ||
                      (selectedOrder.status === 'READY' && s.key === 'READY_TO_SHIP')
                    );
                    const isCompleted = index < currentIndex;
                    const isCurrent = index === currentIndex;

                    return (
                      <div key={step.key} style={styles.progressStep}>
                        <div style={{
                          ...styles.progressDot,
                          ...(isCompleted ? styles.progressDotCompleted : {}),
                          ...(isCurrent ? styles.progressDotCurrent : {}),
                        }}>
                          {isCompleted && '✓'}
                        </div>
                        <div style={{
                          ...styles.progressLabel,
                          ...(isCurrent ? styles.progressLabelCurrent : {}),
                        }}>
                          {step.label}
                        </div>
                        {index < STATUS_FLOW.length - 1 && (
                          <div style={{
                            ...styles.progressLine,
                            ...(isCompleted ? styles.progressLineCompleted : {}),
                          }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* LTL Freight Tracking */}
              <div data-tutorial="tracking-section">
                <ShipmentTracking orderId={selectedOrder.id} orderStatus={selectedOrder.status} />
              </div>

              {/* Status Timeline */}
              {selectedOrder.status_history && selectedOrder.status_history.length > 0 && (
                <div style={styles.timelineSection}>
                  <h3 style={styles.timelineTitle}>Status Timeline</h3>
                  <div style={styles.timeline}>
                    {selectedOrder.status_history.map((entry, index) => (
                      <div key={entry.id} style={styles.timelineItem}>
                        <div style={styles.timelineDot}>
                          {index === 0 ? '●' : '○'}
                        </div>
                        <div style={styles.timelineContent}>
                          <div style={styles.timelineStatus}>{entry.status_display}</div>
                          <div style={styles.timelineDate}>
                            {new Date(entry.changed_at).toLocaleString()}
                          </div>
                          {entry.notes && (
                            <div style={styles.timelineNotes}>{entry.notes}</div>
                          )}
                        </div>
                      </div>
                    ))}
                    {/* Always show order placed */}
                    <div style={styles.timelineItem}>
                      <div style={styles.timelineDot}>○</div>
                      <div style={styles.timelineContent}>
                        <div style={styles.timelineStatus}>Order Placed</div>
                        <div style={styles.timelineDate}>
                          {new Date(selectedOrder.order_date).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={styles.detailsGrid}>
                <div style={styles.detailGroup}>
                  <div style={styles.detailLabel}>Order Date</div>
                  <div style={styles.detailValue}>{new Date(selectedOrder.order_date).toLocaleDateString()}</div>
                </div>
                <div style={styles.detailGroup}>
                  <div style={styles.detailLabel}>Delivery Date</div>
                  <div style={styles.detailValue}>{new Date(selectedOrder.delivery_date).toLocaleDateString()}</div>
                </div>
                <div style={styles.detailGroup}>
                  <div style={styles.detailLabel}>Shipping Method</div>
                  <div style={styles.detailValue}>{selectedOrder.shipping_method}</div>
                </div>
                {selectedOrder.customer_po_number && (
                  <div style={styles.detailGroup}>
                    <div style={styles.detailLabel}>PO Number</div>
                    <div style={styles.detailValue}>{selectedOrder.customer_po_number}</div>
                  </div>
                )}
                {selectedOrder.is_rush && (
                  <div style={styles.detailGroup}>
                    <div style={styles.detailLabel}>Rush Order</div>
                    <div style={styles.detailValue}>Yes</div>
                  </div>
                )}
              </div>

              {selectedOrder.special_instructions && (
                <div style={styles.instructionsBox}>
                  <div style={styles.detailLabel}>Special Instructions</div>
                  <div style={styles.detailValue}>{selectedOrder.special_instructions}</div>
                </div>
              )}

              {user?.permissions.can_place_orders && (
                <div style={styles.reorderButtonContainer}>
                  <button
                    onClick={() => handleReorder(selectedOrder)}
                    style={styles.reorderButtonLarge}
                  >
                    Reorder These Items
                  </button>
                </div>
              )}

              <h3 style={styles.itemsTitle}>Order Items</h3>
              <table style={styles.itemsTable}>
                <thead>
                  <tr>
                    <th style={styles.th}>Formula</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Quantity</th>
                    <th style={styles.th}>Packages</th>
                    <th style={styles.th}>Pricing</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items.map((item) => (
                    <tr key={item.id}>
                      <td style={styles.td}>
                        <div>
                          {item.store_product_name || item.formula_name}
                          {item.item_source === 'store' && <span style={{ marginLeft: '4px', padding: '1px 6px', borderRadius: '3px', background: '#dbeafe', color: '#1d4ed8', fontSize: '10px', fontWeight: 600 }}>(Store)</span>}
                        </div>
                        {item.item_source !== 'store' && (item.feed_grade || item.animal_type) && (
                          <div style={styles.itemMeta}>
                            {[item.feed_grade, item.animal_type].filter(Boolean).join(' - ')}
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>{item.is_pellet ? 'Pellet' : 'Mash'}</td>
                      <td style={styles.td}>
                        {item.total_quantity_lbs > 0
                          ? `${item.total_quantity_lbs.toLocaleString()} lbs`
                          : item.number_of_packages > 0
                            ? `${item.number_of_packages} bags`
                            : '—'}
                      </td>
                      <td style={styles.td}>
                        {item.number_of_packages > 0 && item.package_size_lbs > 0
                          ? `${item.number_of_packages} × ${item.package_size_lbs} lbs`
                          : item.number_of_packages > 0
                            ? `${item.number_of_packages} bags`
                            : '—'}
                      </td>
                      <td style={styles.td}>{formatItemPricing(item)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={styles.totalsBox}>
                {selectedOrder.pallet_discount && selectedOrder.pallet_discount.savings > 0 && (
                  <div style={{ ...styles.totalRow, color: '#166534' }}>
                    <span>Pallet savings ({selectedOrder.pallet_discount.pallets} pallets, ${selectedOrder.pallet_discount.per_bag.toFixed(2)} off each bag):</span>
                    <span>-${selectedOrder.pallet_discount.savings.toFixed(2)}</span>
                  </div>
                )}
                <div style={styles.totalRow}>
                  <span>Subtotal:</span>
                  <span>${parseFloat(selectedOrder.subtotal).toFixed(2)}</span>
                </div>
                {!selectedOrder.is_tax_exempt && (
                  <div style={styles.totalRow}>
                    <span>Tax:</span>
                    <span>${parseFloat(selectedOrder.tax_amount).toFixed(2)}</span>
                  </div>
                )}
                <div style={{...styles.totalRow, ...styles.grandTotal}}>
                  <span>Total:</span>
                  <span>${parseFloat(selectedOrder.total_amount).toFixed(2)}</span>
                </div>
                <div style={styles.totalRow}>
                  <span>Total Weight:</span>
                  <span>{selectedOrder.total_weight_lbs.toLocaleString()} lbs</span>
                </div>
                <div style={styles.totalRow}>
                  <span>Estimated Pallets:</span>
                  <span>{selectedOrder.estimated_pallets}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      {cancelConfirmOpen && selectedOrder && (
        <div style={styles.modal} onClick={() => setCancelConfirmOpen(false)}>
          <div style={styles.confirmDialog} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.confirmTitle}>Cancel Order?</h3>
            <p style={styles.confirmText}>
              Are you sure you want to cancel Order #{selectedOrder.order_number}
              {selectedOrder.total_amount ? ` ($${parseFloat(selectedOrder.total_amount).toFixed(2)})` : ''}?
            </p>
            <p style={styles.confirmSubtext}>
              This will remove the order from your delivery schedule.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation (optional)"
              style={styles.cancelReasonInput}
              rows={3}
            />
            <div style={styles.confirmButtons}>
              <button
                onClick={() => { setCancelConfirmOpen(false); setCancelReason(''); }}
                style={styles.keepOrderButton}
                disabled={saving}
              >
                Keep Order
              </button>
              <button
                onClick={handleCancelOrder}
                style={styles.confirmCancelButton}
                disabled={saving}
              >
                {saving ? 'Cancelling...' : 'Cancel Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message Toast (outside modal) */}
      {successMessage && !selectedOrder && (
        <div style={styles.toast}>{successMessage}</div>
      )}

      <PortalFooter />
    </div>
  );
};

const getStatusIcon = (status: string): string => {
  switch (status) {
    case 'PENDING':
    case 'DRAFT':
      return '📋';
    case 'CONFIRMED':
      return '✅';
    case 'IN_PRODUCTION':
    case 'PRODUCTION':
      return '🏭';
    case 'MILLED':
      return '✨';
    case 'PACKAGING':
      return '📦';
    case 'READY_TO_SHIP':
    case 'READY':
      return '🚚';
    case 'SHIPPED':
      return '🚛';
    case 'DELIVERED':
      return '🎉';
    default:
      return '📋';
  }
};

const getStatusStyle = (status: string): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    padding: '0.35rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.875rem',
    fontWeight: '500',
    display: 'inline-block',
  };

  switch (status) {
    case 'PENDING':
    case 'DRAFT':
      return { ...baseStyle, backgroundColor: '#fef3c7', color: '#92400e' };
    case 'CONFIRMED':
      return { ...baseStyle, backgroundColor: '#dbeafe', color: '#1e40af' };
    case 'IN_PRODUCTION':
    case 'PRODUCTION':
      return { ...baseStyle, backgroundColor: '#e0e7ff', color: '#3730a3' };
    case 'MILLED':
      return { ...baseStyle, backgroundColor: '#c7d2fe', color: '#4338ca' };
    case 'PACKAGING':
      return { ...baseStyle, backgroundColor: '#fce7f3', color: '#9d174d' };
    case 'READY_TO_SHIP':
    case 'READY':
      return { ...baseStyle, backgroundColor: '#ccfbf1', color: '#0f766e' };
    case 'SHIPPED':
      return { ...baseStyle, backgroundColor: '#d1fae5', color: '#047857' };
    case 'DELIVERED':
      return { ...baseStyle, backgroundColor: '#d1fae5', color: '#065f46' };
    case 'CANCELLED':
      return { ...baseStyle, backgroundColor: '#fee2e2', color: '#991b1b' };
    default:
      return { ...baseStyle, backgroundColor: 'rgba(195, 173, 139, 0.2)', color: '#3F2021', border: '1px solid #C3AD8B' };
  }
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '2rem 1.5rem',
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: 0,
  },
  table: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    boxShadow: '0 2px 4px rgba(63, 32, 33, 0.1)',
    overflowX: 'auto',
  },
  tableElement: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    borderBottom: '2px solid #C3AD8B',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#3F2021',
    textTransform: 'uppercase',
    backgroundColor: '#FAF9F6',
  },
  td: {
    padding: '1rem',
    borderBottom: '1px solid #C3AD8B',
    fontSize: '0.95rem',
    color: '#3F2021',
  },
  buttonGroup: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  viewButton: {
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  reorderButton: {
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  reorderButtonContainer: {
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #C3AD8B',
  },
  reorderButtonLarge: {
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 2rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    width: '100%',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(108, 128, 98, 0.3)',
  },
  emptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    boxShadow: '0 2px 4px rgba(63, 32, 33, 0.1)',
    padding: '3rem',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: '1.125rem',
    color: '#508590',
    marginBottom: '1.5rem',
  },
  placeOrderButton: {
    display: 'inline-block',
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    textDecoration: 'none',
    fontSize: '1rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(231, 60, 62, 0.3)',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    fontSize: '1.125rem',
    color: '#508590',
  },
  error: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '4px',
    textAlign: 'center',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(63, 32, 33, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    width: '90%',
    maxWidth: '900px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '2px solid #C3AD8B',
    backgroundColor: '#3F2021',
  },
  modalTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#FAF9F6',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: '#FAF9F6',
    lineHeight: 1,
  },
  modalBody: {
    padding: '1.5rem',
    backgroundColor: '#FAF9F6',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1.5rem',
    marginBottom: '1.5rem',
  },
  detailGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  detailLabel: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#508590',
    textTransform: 'uppercase',
  },
  detailValue: {
    fontSize: '1rem',
    color: '#3F2021',
  },
  instructionsBox: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #C3AD8B',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1.5rem',
  },
  itemsTitle: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: '#3F2021',
    marginBottom: '1rem',
  },
  itemsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '1.5rem',
    backgroundColor: '#FFFFFF',
    border: '1px solid #C3AD8B',
    borderRadius: '4px',
  },
  itemMeta: {
    fontSize: '0.8rem',
    color: '#508590',
    marginTop: '0.25rem',
  },
  totalsBox: {
    backgroundColor: '#FFFFFF',
    border: '2px solid #C3AD8B',
    padding: '1rem',
    borderRadius: '4px',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    fontSize: '0.95rem',
    color: '#3F2021',
  },
  grandTotal: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    borderTop: '2px solid #E73C3E',
    paddingTop: '0.75rem',
    marginTop: '0.5rem',
    color: '#E73C3E',
  },
  // Status Banner styles
  statusBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem 1.5rem',
    backgroundColor: '#FFFFFF',
    border: '2px solid #C3AD8B',
    borderRadius: '8px',
    marginBottom: '1.5rem',
  },
  statusBannerIcon: {
    fontSize: '2rem',
  },
  statusBannerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  statusBannerLabel: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#508590',
    textTransform: 'uppercase',
  },
  // Progress Bar styles
  progressContainer: {
    backgroundColor: '#FFFFFF',
    padding: '1.5rem',
    border: '1px solid #C3AD8B',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    overflowX: 'auto',
  },
  progressBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    minWidth: '600px',
    position: 'relative',
  },
  progressStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    position: 'relative',
  },
  progressDot: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#e5e7eb',
    border: '2px solid #d1d5db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    color: '#fff',
    zIndex: 1,
  },
  progressDotCompleted: {
    backgroundColor: '#6C8062',
    borderColor: '#6C8062',
  },
  progressDotCurrent: {
    backgroundColor: '#E73C3E',
    borderColor: '#E73C3E',
    boxShadow: '0 0 0 4px rgba(231, 60, 62, 0.2)',
  },
  progressLabel: {
    marginTop: '0.5rem',
    fontSize: '0.75rem',
    color: '#6b7280',
    textAlign: 'center',
    maxWidth: '80px',
  },
  progressLabelCurrent: {
    color: '#E73C3E',
    fontWeight: '600',
  },
  progressLine: {
    position: 'absolute',
    top: '12px',
    left: '50%',
    width: '100%',
    height: '2px',
    backgroundColor: '#e5e7eb',
    zIndex: 0,
  },
  progressLineCompleted: {
    backgroundColor: '#6C8062',
  },
  // Tracking Box styles
  trackingBox: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #86efac',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  trackingHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.75rem',
  },
  trackingIcon: {
    fontSize: '1.25rem',
  },
  trackingTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#166534',
  },
  trackingDetails: {
    fontSize: '0.95rem',
    color: '#3F2021',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  // Timeline styles
  timelineSection: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #C3AD8B',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  timelineTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#3F2021',
    marginBottom: '1rem',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  timelineItem: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'flex-start',
  },
  timelineDot: {
    color: '#6C8062',
    fontSize: '0.75rem',
    lineHeight: '1.5rem',
  },
  timelineContent: {
    flex: 1,
  },
  timelineStatus: {
    fontSize: '0.95rem',
    fontWeight: '500',
    color: '#3F2021',
  },
  timelineDate: {
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  timelineNotes: {
    fontSize: '0.85rem',
    color: '#508590',
    marginTop: '0.25rem',
    fontStyle: 'italic',
  },
  // Action buttons row
  actionButtonsRow: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
  },
  editButton: {
    backgroundColor: '#508590',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  cancelOrderButton: {
    backgroundColor: 'transparent',
    color: '#991b1b',
    border: '2px solid #991b1b',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  saveButton: {
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  cancelEditButton: {
    backgroundColor: 'transparent',
    color: '#3F2021',
    border: '2px solid #C3AD8B',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  nonModifiableBanner: {
    backgroundColor: '#fef3c7',
    border: '1px solid #f59e0b',
    color: '#92400e',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    fontSize: '0.95rem',
  },
  successBanner: {
    backgroundColor: '#d1fae5',
    border: '1px solid #6ee7b7',
    color: '#065f46',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.95rem',
    fontWeight: '500',
  },
  warningBanner: {
    backgroundColor: '#fef3c7',
    border: '1px solid #fbbf24',
    color: '#92400e',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.95rem',
  },
  // Edit form styles
  editForm: {
    backgroundColor: '#FFFFFF',
    border: '2px solid #508590',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  editFormTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#3F2021',
    marginBottom: '1rem',
    marginTop: 0,
  },
  editFormGrid: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  formLabel: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#508590',
    textTransform: 'uppercase' as const,
  },
  formInput: {
    padding: '0.75rem',
    border: '2px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '1rem',
    color: '#3F2021',
    backgroundColor: '#FAF9F6',
  },
  formTextarea: {
    padding: '0.75rem',
    border: '2px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '1rem',
    color: '#3F2021',
    backgroundColor: '#FAF9F6',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
  },
  // Confirm dialog styles
  confirmDialog: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    padding: '2rem',
    width: '90%',
    maxWidth: '450px',
    textAlign: 'center' as const,
  },
  confirmTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#3F2021',
    marginTop: 0,
    marginBottom: '1rem',
  },
  confirmText: {
    fontSize: '1rem',
    color: '#3F2021',
    marginBottom: '0.5rem',
  },
  confirmSubtext: {
    fontSize: '0.9rem',
    color: '#6b7280',
    marginBottom: '1.5rem',
  },
  cancelReasonInput: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontFamily: 'inherit',
    resize: 'vertical' as const,
    marginBottom: '1.5rem',
    boxSizing: 'border-box' as const,
  },
  confirmButtons: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
  },
  keepOrderButton: {
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
  },
  confirmCancelButton: {
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
  },
  toast: {
    position: 'fixed' as const,
    bottom: '2rem',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#065f46',
    color: '#FFFFFF',
    padding: '1rem 2rem',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    zIndex: 1100,
    fontSize: '1rem',
    fontWeight: '500',
  },
  // Item editing styles
  itemsEditSection: {
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #C3AD8B',
  },
  itemsEditHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  itemsEditTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#3F2021',
    margin: 0,
  },
  addItemButton: {
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
  },
  itemEditRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem',
    backgroundColor: '#FAF9F6',
    border: '1px solid #C3AD8B',
    borderRadius: '4px',
    marginBottom: '0.5rem',
  },
  itemEditRowRemoved: {
    opacity: 0.5,
    backgroundColor: '#fee2e2',
    borderColor: '#fca5a5',
  },
  itemEditInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
    flex: 1,
  },
  itemEditName: {
    fontWeight: '500',
    color: '#3F2021',
    fontSize: '0.95rem',
  },
  itemEditMeta: {
    fontSize: '0.8rem',
    color: '#508590',
  },
  itemEditActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  itemRemovedActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  removedLabel: {
    color: '#991b1b',
    fontSize: '0.875rem',
    fontWeight: '500',
  },
  undoButton: {
    backgroundColor: '#508590',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.35rem 0.75rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  quantityInput: {
    width: '100px',
    padding: '0.5rem',
    border: '2px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '0.95rem',
    textAlign: 'right' as const,
  },
  quantityLabel: {
    color: '#508590',
    fontSize: '0.875rem',
  },
  removeItemButton: {
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    border: 'none',
    width: '28px',
    height: '28px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addItemForm: {
    backgroundColor: '#e0f2f1',
    border: '2px solid #508590',
    borderRadius: '8px',
    padding: '1rem',
    marginTop: '1rem',
  },
  addItemTitle: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#3F2021',
    marginTop: 0,
    marginBottom: '0.75rem',
  },
  addItemFields: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
    marginBottom: '0.75rem',
  },
  feedSelect: {
    flex: 2,
    minWidth: '200px',
    padding: '0.5rem',
    border: '2px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '0.9rem',
    backgroundColor: '#FFFFFF',
  },
  addQuantityInput: {
    width: '120px',
    padding: '0.5rem',
    border: '2px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  packageSelect: {
    width: '100px',
    padding: '0.5rem',
    border: '2px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '0.9rem',
    backgroundColor: '#FFFFFF',
  },
  addItemButtons: {
    display: 'flex',
    gap: '0.5rem',
  },
  confirmAddButton: {
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
  },
  cancelAddButton: {
    backgroundColor: 'transparent',
    color: '#3F2021',
    border: '1px solid #C3AD8B',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
};

export default PortalOrders;
